import React, { createContext, useState, useEffect, ReactNode } from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signOut,
  sendPasswordResetEmail,
  confirmPasswordReset,
  applyActionCode,
  User as FirebaseUser,
  ActionCodeSettings,
} from "firebase/auth";
import { auth } from "firebaseConfig";
import { apiLogin, apiRegister } from "api/client";
import {
  DEV_AUTH_CHANGE_EVENT,
  getDevAuthSession,
  isDevAuthEnabled,
  makeFakeFirebaseUser,
  setDevAuthSession,
} from "devAuth";

/**
 * Extend FirebaseUser to optionally carry backend role info.
 * This avoids TypeScript errors and supports admin/superadmin checks later.
 */
export interface AppUser extends FirebaseUser {
  is_admin?: boolean;
}

interface AuthContextType {
  user: AppUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  sendResetEmail: (email: string) => Promise<void>;
  confirmReset: (code: string, newPassword: string) => Promise<void>;
  verifyEmail: (code: string) => Promise<void>;
  resendVerification: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Firebase's own page confirms the link, then sends the user back to sign in.
// The in-app /verify-email page went with the customer pages.
const emailVerificationSettings = (): ActionCodeSettings => ({
  url: `${window.location.origin}/login`,
});

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isDevAuthEnabled()) {
      const sync = () => {
        const session = getDevAuthSession();
        setUser(session ? makeFakeFirebaseUser(session) : null);
        setLoading(false);
      };
      sync();
      window.addEventListener(DEV_AUTH_CHANGE_EVENT, sync);
      return () => window.removeEventListener(DEV_AUTH_CHANGE_EVENT, sync);
    }

    const unsubscribe = auth.onAuthStateChanged(
      (firebaseUser: FirebaseUser | null) => {
        setUser(firebaseUser as AppUser);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, []);

  const login = async (email: string, password: string) => {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    const u = credential.user;

    // Enforce verified email before allowing navigation or backend handshake
    if (!u.emailVerified) {
      try {
        await sendEmailVerification(u, emailVerificationSettings());
      } catch {
        // ignore resend failures
      }
      throw new Error(
        "Please verify your email before continuing. We've sent you a new verification link."
      );
    }

    // Signing in again as the same account may not fire onAuthStateChanged, so
    // take the fresh (now verified) user from the credential.
    setUser(u as AppUser);

    // Only talk to backend if email is verified
    if (auth.currentUser?.emailVerified) {
      try {
        await apiLogin();
      } catch (err: any) {
        // If user not yet in backend, register then login
        if (err?.status === 404) {
          try {
            await apiRegister(true);
            await apiLogin();
          } catch {
            // Non-fatal; UI flows can refetch profile
          }
        }
      }
    }
  };

  const register = async (email: string, password: string) => {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    if (credential.user) {
      await sendEmailVerification(credential.user, emailVerificationSettings());
      // Do not call backend until email verified
    }
  };

  const logout = async () => {
    if (isDevAuthEnabled()) {
      setDevAuthSession(null);
      return;
    }
    await signOut(auth);
  };

  const sendResetEmail = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const confirmReset = async (code: string, newPassword: string) => {
    await confirmPasswordReset(auth, code, newPassword);
  };

  const verifyEmail = async (code: string) => {
    await applyActionCode(auth, code);
  };

  const resendVerification = async () => {
    if (!auth.currentUser) throw new Error("Sign in again, then resend the email.");
    await sendEmailVerification(auth.currentUser, emailVerificationSettings());
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        sendResetEmail,
        confirmReset,
        verifyEmail,
        resendVerification,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
